import React from 'react';
import { motion } from 'framer-motion';
import { FiPackage, FiTruck, FiGlobe, FiClock, FiShield, FiDollarSign } from 'react-icons/fi';
import './Services.css';

const Services = () => {
  const services = [
    {
      icon: FiPackage,
      title: 'Express Delivery',
      description:
        'Same-day and next-day delivery options for urgent shipments. Get your packages delivered when you need them most.',
      features: ['Same-day delivery', 'Real-time tracking', 'Priority handling'],
    },
    {
      icon: FiTruck,
      title: 'Freight Services',
      description:
        'Heavy-duty freight transportation for large shipments. Reliable and secure handling of bulk cargo.',
      features: ['Large capacity', 'Secure handling', 'Flexible scheduling'],
    },
    {
      icon: FiGlobe,
      title: 'International Shipping',
      description:
        'Global shipping network connecting you to destinations worldwide. Fast and reliable international delivery.',
      features: ['Worldwide coverage', 'Customs clearance', 'Tracking included'],
    },
    {
      icon: FiClock,
      title: 'Scheduled Delivery',
      description:
        'Plan your deliveries in advance with our scheduled service. Perfect for recurring shipments.',
      features: ['Flexible scheduling', 'Recurring options', 'Priority booking'],
    },
    {
      icon: FiShield,
      title: 'Secure Shipping',
      description:
        'Extra security for valuable items. Special handling and insurance options available.',
      features: ['Insurance options', 'Secure packaging', 'Special handling'],
    },
    {
      icon: FiDollarSign,
      title: 'Economy Shipping',
      description:
        'Cost-effective shipping solutions for standard deliveries. Quality service at affordable prices.',
      features: ['Budget-friendly', 'Reliable service', 'Standard delivery'],
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  return (
    <div className="services page-transition">
      {/* Hero Section */}
      <section className="services-hero">
        <div className="container">
          <motion.div
            className="services-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="services-hero-title">Our Services</h1>
            <p className="services-hero-subtitle">
              Comprehensive logistics solutions tailored to your needs
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="services-section">
        <div className="container">
          <motion.div
            className="services-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="service-card"
                variants={itemVariants}
                whileHover={{ y: -10, scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <div className="service-icon">
                  <service.icon size={40} />
                </div>
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>
                <ul className="service-features">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="service-feature">
                      <span className="service-feature-icon">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Process Section */}
      <section className="services-process">
        <div className="container">
          <motion.div
            className="process-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">How It Works</h2>
            <p className="section-description">
              Simple steps to get your package delivered
            </p>
          </motion.div>

          <motion.div
            className="process-steps"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {[
              {
                step: '01',
                title: 'Book Your Shipment',
                description: 'Fill out our simple booking form with your package details.',
              },
              {
                step: '02',
                title: 'We Pick Up',
                description: 'Our team collects your package at your preferred location.',
              },
              {
                step: '03',
                title: 'Safe Transport',
                description: 'Your package is securely transported to its destination.',
              },
              {
                step: '04',
                title: 'Quick Delivery',
                description: 'Receive your package quickly and safely at the destination.',
              },
            ].map((step, index) => (
              <motion.div
                key={index}
                className="process-step"
                variants={itemVariants}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
              >
                <div className="process-step-number">{step.step}</div>
                <h3 className="process-step-title">{step.title}</h3>
                <p className="process-step-description">{step.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;

